import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, User, Users, MessageSquare, ArrowRight, Loader2 } from "lucide-react";
import { i18n } from "../../../translate/i18n";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import useQueues from "../../../hooks/useQueues";

interface UserOption {
  id: number;
  name: string;
  email?: string;
  profile?: string;
  queues?: Queue[];
}

interface Queue {
  id: number;
  name: string;
  color?: string;
}

interface TransferTicketModalProps {
  open: boolean;
  onClose: () => void;
  ticketId: number | null;
  ticket: any;
  onTransferSuccess?: () => void;
}

export function TransferTicketModal({
  open,
  onClose,
  ticketId,
  ticket,
  onTransferSuccess,
}: TransferTicketModalProps) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [allQueues, setAllQueues] = useState<Queue[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<number | "">("");
  const [msgTransfer, setMsgTransfer] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load all queues on mount
  useEffect(() => {
    if (isMounted.current && open) {
      const loadQueues = async () => {
        try {
          const list = await findAllQueues();
          setAllQueues(list);
          setQueues(list);
        } catch (err) {
          toastError(err);
        }
      };
      loadQueues();
    }
  }, [open]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchParam("");
      setSelectedUser(null);
      setSelectedQueue("");
      setMsgTransfer("");
      setUsers([]);
      setShowUserDropdown(false);
    }
  }, [open]);

  // Search users with debounce
  useEffect(() => {
    if (!open || searchParam.length < 3) {
      setLoading(false);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          if (isMounted.current) {
            setUsers(data.users);
            setShowUserDropdown(true);
          }
        } catch (err) {
          toastError(err);
        } finally {
          if (isMounted.current) {
            setLoading(false);
          }
        }
      };
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open]);

  const handleSelectUser = (user: UserOption) => {
    setSelectedUser(user);
    setSearchParam(user.name);
    setShowUserDropdown(false);

    if (user.queues && Array.isArray(user.queues)) {
      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id);
      } else {
        setSelectedQueue("");
      }
      setQueues(user.queues);
    } else {
      setQueues(allQueues);
      setSelectedQueue("");
    }
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setSearchParam("");
    setQueues(allQueues);
    setSelectedQueue("");
    searchInputRef.current?.focus();
  };

  const handleClose = () => {
    onClose();
  };

  const handleTransfer = async () => {
    if (!ticketId || !selectedQueue) return;

    setLoading(true);
    try {
      const data = {
        userId: selectedUser?.id || null,
        status: !selectedUser ? "pending" : ticket?.isGroup ? "group" : "open",
        queueId: selectedQueue,
        msgTransfer: msgTransfer || null,
        isTransfered: true,
      };

      await api.put(`/tickets/${ticketId}`, data);
      onTransferSuccess?.();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg mx-4 overflow-hidden rounded-2xl bg-[var(--card-bg)] shadow-2xl border border-[var(--border-color)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--border-color)] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
                <ArrowRight className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                  {i18n.t("v2.transferModal.title")}
                </h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  {i18n.t("v2.transferModal.subtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* User Search */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
                <User className="h-4 w-4 text-[var(--text-secondary)]" />
                {i18n.t("v2.transferModal.userLabel")}
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-secondary)]" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchParam}
                    onChange={(e) => {
                      setSearchParam(e.target.value);
                      if (selectedUser) {
                        setSelectedUser(null);
                        setQueues(allQueues);
                      }
                    }}
                    placeholder={i18n.t("v2.transferModal.userPlaceholder")}
                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] py-3 pl-10 pr-10 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {loading && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--text-secondary)]" />
                  )}
                  {selectedUser && !loading && (
                    <button
                      onClick={handleClearUser}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* User Dropdown */}
                <AnimatePresence>
                  {showUserDropdown && users.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-10 mt-2 w-full overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-lg"
                    >
                      <div className="max-h-48 overflow-y-auto">
                        {users.map((user) => (
                          <button
                            key={user.id}
                            onClick={() => handleSelectUser(user)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-tertiary)]"
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/10">
                              <User className="h-4 w-4 text-blue-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[var(--text-primary)]">
                                {user.name}
                              </p>
                              {user.email && (
                                <p className="text-xs text-[var(--text-secondary)]">
                                  {user.email}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {showUserDropdown && users.length === 0 && searchParam.length >= 3 && !loading && (
                  <div className="absolute z-10 mt-2 w-full rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 text-center text-sm text-[var(--text-secondary)] shadow-lg">
                    {i18n.t("v2.transferModal.noUsersFound")}
                  </div>
                )}
              </div>
              <p className="text-xs text-[var(--text-secondary)]">
                {i18n.t("v2.transferModal.userHint")}
              </p>
            </div>

            {/* Queue Select */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
                <Users className="h-4 w-4 text-[var(--text-secondary)]" />
                {i18n.t("v2.transferModal.queueLabel")}
              </label>
              <select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value ? Number(e.target.value) : "")}
                className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 text-sm text-[var(--text-primary)] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">{i18n.t("v2.transferModal.queuePlaceholder")}</option>
                {queues.map((queue) => (
                  <option key={queue.id} value={queue.id}>
                    {queue.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Transfer Message */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
                <MessageSquare className="h-4 w-4 text-[var(--text-secondary)]" />
                {i18n.t("v2.transferModal.messageLabel")}
              </label>
              <textarea
                value={msgTransfer}
                onChange={(e) => setMsgTransfer(e.target.value)}
                placeholder={i18n.t("v2.transferModal.messagePlaceholder")}
                rows={4}
                className="w-full resize-none rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-[var(--border-color)] px-6 py-4">
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-xl border border-[var(--border-color)] px-4 py-2.5 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
            >
              {i18n.t("v2.transferModal.cancel")}
            </button>
            <button
              onClick={handleTransfer}
              disabled={loading || !selectedQueue}
              className="flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {i18n.t("v2.transferModal.transfer")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
